import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { t } from 'i18next';
import screenResolution from '../../../../../utils/screen-resolution';
import NestedDropdown from '../../../../../lib/ui/form_elements/NestedDropdown';
import TextInput from '../../../../../lib/ui/form_elements/TextInput';
import { Error as FieldError } from '../../../../../lib/ui/form_elements/TextInput/css';
import Checkbox from '../../../../../lib/ui/form_elements/Checkbox';
import {
  Container,
  DangerousGoodsCheckboxContainer,
  DangerousGoodsContainer,
  DeleteItemButton,
  FieldsRow1,
  FieldsRow2,
  FieldsRow3,
  ItemBody,
  ItemHeader,
  ItemTitle,
  MeasurementsContainer,
} from './css';
import Tooltip from '../../../../../lib/ui/form_elements/Tooltip';
import { UnitType, maxWeightImperial, maxWeightMetric } from '../../../../../utils/enums';
import {
  isRequired,
  validateIntInRange,
  isValidWeight,
  maxChar75,
} from '../../../../../utils/validator';
import { numbersAndTwoDecimals } from '../../../../../utils/mask';

const isBetween1And999 = validateIntInRange(1, 999);

const isBetween1And999999 = validateIntInRange(1, 999999);

const Item = ({
  // eslint-disable-next-line no-unused-vars
  item, index, onRemove, itemDangerous, selectedUnits, commoditiesList, usShipment, density, measurementList,
  commodityType,
}) => {
  const resolution = screenResolution();

  const commodityTypeName = commodityType && (typeof commodityType === 'object' ? commodityType.name : commodityType);
  const isDGClass = commodityTypeName && commodityTypeName.toLowerCase().includes('dg');
  const showDGClassError = usShipment && itemDangerous && !isDGClass;

  const inputSkid = measurementList.SkidQty;
  const inputPiece = measurementList.PieceQty;

  const isImperialUnits = (selectedUnits && selectedUnits.indexOf(t('BtnImperial')) !== -1);

  const commodities = commoditiesList.children.map((category) => ({
    value: category.title.value.toLowerCase(),
    children: category.children.map((commodity) => ({
      code: commodity.code.value || undefined,
      value: commodity.description.value.toLowerCase(),
    })),
  }));

  const handleRemoveItem = (e) => {
    e.preventDefault();
    onRemove(index);
  };

  const validWeightValidator = useMemo(() => {
    const isImperialUnit = selectedUnits === t(UnitType.Imperial);
    let maxWeight;
    if (usShipment) {
      maxWeight = isImperialUnit ? maxWeightImperial.US : maxWeightMetric.US;
    } else {
      maxWeight = isImperialUnit ? maxWeightImperial.nonUS : maxWeightMetric.nonUS;
    }

    return isValidWeight(maxWeight, isImperialUnit);
  }, [selectedUnits]);

  const isSkidOptionalField = !inputSkid && !inputPiece ? [isBetween1And999, isRequired] : [isBetween1And999];
  const isPieceOptionalField = !inputSkid && !inputPiece ? [isBetween1And999, isRequired] : [isBetween1And999];

  return (
    <Container>
      <ItemHeader>
        <ItemTitle>
          {t('Item')}
          {' '}
          {`${index + 1}`}
        </ItemTitle>
        <DeleteItemButton onClick={handleRemoveItem}>{t('Delete')}</DeleteItemButton>
      </ItemHeader>
      <ItemBody>

        <FieldsRow1>
          <Field
            name={`${item}.commodityType`}
            label={t('CommodityType')}
            component={NestedDropdown}
            resolution={resolution}
            options={commodities}
            placeholder={t('Select')}
            validate={usShipment ? [isRequired] : null}
            search
            optional={!usShipment}
          />
          <Field
            name={`${item}.description`}
            label={t('Description')}
            component={TextInput}
            maxLength={75}
            validate={[isRequired, maxChar75]}
          />
          {showDGClassError && (
            <FieldError style={{ gridColumn: '1', gridRow: '2', margin: '-20px 0 0 0' }}>
              {t('DGClassNote')}
            </FieldError>
          )}
        </FieldsRow1>

        {
                    (usShipment)
                      ? (
                        <FieldsRow3>
                          <Field
                            name={`${item}.itemNMFC`}
                            label={t('NmfcCode')}
                            component={TextInput}
                            maxLength={6}
                            validate={[isBetween1And999999]}
                            optional
                          />
                        </FieldsRow3>
                      )
                      : <FieldsRow3 />
                }

        <FieldsRow2>
          <Field
            name={`${item}.SkidQty`}
            label={t('SkidQty')}
            component={TextInput}
            maxLength={3}
            validate={isSkidOptionalField}
          />
          <Field
            name={`${item}.PieceQty`}
            label={t('PieceQty')}
            component={TextInput}
            maxLength={3}
            validate={isPieceOptionalField}
          />
        </FieldsRow2>

        <MeasurementsContainer>
          <Field
            name={`${item}.itemLength`}
            label={`${t('ItemLength')} (${selectedUnits === t(UnitType.Imperial) ? t('InchesShorten') : 'cm'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired]}
            normalize={numbersAndTwoDecimals}
          />
          <Field
            name={`${item}.itemWidth`}
            label={`${t('ItemWidth')} (${selectedUnits === t(UnitType.Imperial) ? t('InchesShorten') : 'cm'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired]}
            normalize={numbersAndTwoDecimals}
          />
          <Field
            name={`${item}.itemHeight`}
            label={`${t('ItemHeight')} (${selectedUnits === t(UnitType.Imperial) ? t('InchesShorten') : 'cm'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired]}
            normalize={numbersAndTwoDecimals}
          />
          <Field
            name={`${item}.itemWeight`}
            label={`${t('TotalWeight')} (${selectedUnits === t(UnitType.Imperial) ? 'lb' : 'kg'})`}
            component={TextInput}
            maxLength={6}
            placeholder="0.00"
            validate={[isRequired, validWeightValidator]}
            normalize={numbersAndTwoDecimals}
          />
        </MeasurementsContainer>

        <DangerousGoodsContainer>
          <DangerousGoodsCheckboxContainer>
            {(density) ? (
              <p>
                {`${t('VolumeDensity')}: ${density} ${(isImperialUnits)
                  ? t('VolumeDensityImperial') : t('VolumeDensityMetric')}`}
              </p>
            ) : <p />}

            <Field
              name={`${item}.itemDangerous`}
              component={Checkbox}
              label={t('DangerousGoods')}
              tooltip={<Tooltip message={t('TipDangerousGoods')} />}
            />
          </DangerousGoodsCheckboxContainer>

        </DangerousGoodsContainer>
      </ItemBody>
    </Container>
  );
};

Item.propTypes = {
  item: PropTypes.string,
  index: PropTypes.number,
  onRemove: PropTypes.func,
  itemDangerous: PropTypes.bool,
  selectedUnits: PropTypes.string,
  commodityType: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ name: PropTypes.string, code: PropTypes.string })]),
  commoditiesList: PropTypes.shape({
    children: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.shape({
        value: PropTypes.string,
      }),
      children: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string,
        description: PropTypes.shape({
          value: PropTypes.string,
        }),
      })),
    })),
  }),
  usShipment: PropTypes.bool,
  density: PropTypes.number,
  measurementList: PropTypes.shape({
    SkidQty: PropTypes.number,
    PieceQty: PropTypes.number,
  }),
};

export default Item;
